import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, LayoutGrid, Activity, Apple, Target, Ruler, Music, Crown, User, Sparkles,
  Camera, Play, ArrowRight, CheckCircle2, ChevronRight,
} from "lucide-react";

const SECTIONS = [
  {
    id: "getting-started",
    label: "Getting started",
    icon: Sparkles,
    eyebrow: "Step 1",
    title: "Welcome to DAIONE",
    summary:
      "DAIONE is your AI-powered training, nutrition and progress companion. Tell us about you once and we tailor every workout, calorie target and rep around your goal.",
    points: [
      "Sign up takes ~30 seconds. We start a free 30-day trial automatically — no card required.",
      "After signup you'll land in the onboarding wizard: goal, body stats, available equipment, days per week.",
      "Your daily calorie target, macro split and weekly programme are generated from those answers.",
    ],
    cta: { to: "/app/dashboard", label: "Go to Dashboard" },
  },
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutGrid,
    eyebrow: "Step 2",
    title: "Your daily control panel",
    summary:
      "The Dashboard is the home screen — calorie ring, macro breakdown, today's workout, weekly muscle heatmap and your active weight goal all in one place.",
    points: [
      "Calorie ring shows kcal consumed vs. the smart target derived from your active weight goal (TDEE-aware).",
      "Macro pie shows protein/carbs/fat in grams + percentages.",
      "Weekly muscle heatmap turns clickable — tap any group to launch an AI Micro-Workout for it.",
      "Trial banner appears in the last 14 days of your trial — tap it to upgrade.",
    ],
    cta: { to: "/app/dashboard", label: "Open Dashboard" },
  },
  {
    id: "workouts",
    label: "Workouts",
    icon: Activity,
    eyebrow: "Step 3",
    title: "AI-generated multi-week programmes",
    summary:
      "Generate 1–12 week training blocks with progressive overload, then run each session with the focused trainer view.",
    points: [
      "Tap 'Generate plan' and pick weeks (1–12) + session duration (15–180 min). Claude builds the full block.",
      "Each week has a focus + intensity note (e.g. Week 1: foundation 70% intensity).",
      "Switch between weeks using the tab strip — completion marks are isolated per week.",
      "Tap 'Start' on any day to open the focused Session view — track sets, reps, weight, rest timer, PR detection (Epley 1RM).",
      "Use 'Mark done' to log a session without entering the trainer view.",
    ],
    cta: { to: "/app/workout", label: "Open Workouts" },
  },
  {
    id: "nutrition",
    label: "Food log",
    icon: Apple,
    eyebrow: "Step 4",
    title: "Snap, paste, or describe a meal",
    summary:
      "Three ways to log food. The fastest is photo-based — snap a meal, the AI estimates calories and macros instantly.",
    points: [
      "Photo: tap the camera icon, take a picture (native camera on iOS), Claude returns calories + protein/carbs/fat.",
      "Text: describe your meal in natural language (\"2 eggs and 1 toast with butter\") — AI parses portions.",
      "Manual: tap '+ Add manually' and enter known values.",
      "Everything you log syncs with the calorie ring on the Dashboard and the macro totals.",
    ],
    cta: { to: "/app/nutrition", label: "Open Food log" },
  },
  {
    id: "goals",
    label: "Goals",
    icon: Target,
    eyebrow: "Step 5",
    title: "Weight & lift goals with TDEE math",
    summary:
      "Set a goal weight or a target lift number. DAIONE computes your TDEE, recommended daily calorie intake, weeks to goal and shows you the projection chart.",
    points: [
      "Weight goal: pick target weight + timeframe → get recommended_kcal + macro split.",
      "Lift goal: pick exercise + target weight (e.g. 100kg bench) — track current 1RM vs target.",
      "Active goals appear on the Dashboard and shape your daily calorie target automatically.",
      "Use the Share button on any goal card to broadcast progress to Twitter / Facebook / LinkedIn / Instagram.",
    ],
    cta: { to: "/app/goals", label: "Open Goals" },
  },
  {
    id: "measurements",
    label: "Measurements",
    icon: Ruler,
    eyebrow: "Step 6",
    title: "Track your body — beyond the scale",
    summary:
      "Log chest, waist, hips and arms over time. We compute deltas between entries and show the trend so you can see real composition changes.",
    points: [
      "Same-day entries merge automatically — re-submit any time without creating duplicates.",
      "Deltas (e.g. waist −5 cm, chest +3 cm) appear once you have a previous reading in the lookback window.",
      "Shareable progress cards per measurement.",
      "Want photo evidence too? Use the Photos page (next section) to keep a private before/after gallery.",
    ],
    cta: { to: "/app/measurements", label: "Open Measurements" },
  },
  {
    id: "photos",
    label: "Progress photos",
    icon: Camera,
    eyebrow: "Step 6.5",
    title: "Private body-progress gallery",
    summary:
      "A private, JWT-encrypted photo log of your transformation. Only you can view these — no public links, no sharing without your consent.",
    points: [
      "Upload from any device (or use the native iOS camera inside the app).",
      "Tag each photo with weight + caption so you can correlate visual change with scale change.",
      "Reverse-chronological grid + full-screen lightbox with keyboard arrow navigation.",
      "Photos are resized to 1280px JPEG client-side, then stored encrypted at rest. The full-res endpoint requires your JWT.",
    ],
    cta: { to: "/app/progress-photos", label: "Open Photos" },
  },
  {
    id: "music",
    label: "Music",
    icon: Music,
    eyebrow: "Step 7",
    title: "Gym playlist that follows you",
    summary:
      "Connect Spotify once. A persistent music pill appears at the bottom of every in-app page so playback never breaks when you navigate.",
    points: [
      "Tap 'Connect Spotify' on /app/music or your Profile to OAuth.",
      "Paste a playlist URL — we embed the official Spotify player.",
      "The pill stays mounted across page changes — switching from Dashboard to Workout will not restart the song.",
    ],
    cta: { to: "/app/music", label: "Open Music" },
  },
  {
    id: "billing",
    label: "Billing & Trial",
    icon: Crown,
    eyebrow: "Step 8",
    title: "Free for 30 days, then $9.99 AUD / month",
    summary:
      "Every new account gets a 30-day free trial with full DAIONE Pro features. After that, AI generation (workouts, photo meal log, micro-workouts, meal planning) requires a subscription.",
    points: [
      "Trial countdown appears on the Dashboard banner in the last 14 days.",
      "Tap Upgrade to start a real Stripe subscription ($9.99 AUD/mo, auto-renewing).",
      "Manage card / invoices / cancel via the Stripe Customer Portal from /app/billing.",
      "Cancel at any time — your subscription remains active until the end of the current billing period.",
    ],
    cta: { to: "/app/billing", label: "Open Billing" },
  },
  {
    id: "profile",
    label: "Profile settings",
    icon: User,
    eyebrow: "Step 9",
    title: "Tweak the inputs, rerun the AI",
    summary:
      "Profile is where you change the underlying answers that drive every recommendation. Update goal, equipment, days per week — then regenerate your plan to apply the changes.",
    points: [
      "Equipment toggles control which exercises the AI is allowed to use.",
      "Changing 'goal' (build muscle ↔ lose fat) re-shapes both the workout block and the calorie target.",
      "Update your Spotify playlist URL inline here.",
      "Subscription card lets you jump back to Billing.",
    ],
    cta: { to: "/app/profile", label: "Open Profile" },
  },
];

const PRO_TIPS = [
  "Mark sessions complete to keep your weekly muscle heatmap accurate — it's what powers the micro-workout suggestions.",
  "Photo-meal estimation works best when the plate fills most of the frame and lighting is even.",
  "Use the Week tab strip in Workouts to plan ahead — every week's completion is tracked independently.",
  "Goals power your daily calorie target. If the ring feels off, check that your active weight goal is up to date.",
  "Cancel any time from /app/billing — Pro features remain active until the period ends.",
];

export default function TutorialPage() {
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const onScroll = () => {
      // find the section whose top is closest to (and not below) the top of the viewport
      let best = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top < 200) best = s.id;
      }
      setActive(best);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const tocItems = useMemo(
    () => SECTIONS.map((s) => ({ id: s.id, label: s.label, Icon: s.icon })),
    []
  );

  return (
    <div className="px-6 lg:px-10 py-10 max-w-7xl mx-auto" data-testid="tutorial-page">
      {/* Hero */}
      <header className="mb-12 fade-up">
        <div className="inline-flex items-center gap-2 border border-white/10 bg-[#121212] px-3 py-1.5 mb-4">
          <BookOpen className="w-3.5 h-3.5 text-[#6B9BFF]" />
          <span className="label-eyebrow text-white/70">Tutorial</span>
        </div>
        <h1 className="font-display text-5xl sm:text-6xl leading-none mb-4">
          Get the most out of <span className="text-[#6B9BFF]">DAIONE</span>.
        </h1>
        <p className="text-white/60 max-w-2xl text-base sm:text-lg">
          A 9-section walkthrough of every feature. Read it end-to-end or jump to a section. Every section
          ends with a deep link straight into the relevant page.
        </p>
      </header>

      <div className="grid lg:grid-cols-[260px_minmax(0,1fr)] gap-10">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block" data-testid="tutorial-toc">
          <div className="sticky top-6 border border-white/10 bg-[#121212] p-2">
            <div className="label-eyebrow text-white/40 px-3 py-2">Contents</div>
            <nav className="space-y-1">
              {tocItems.map(({ id, label, Icon }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-wider font-bold border-l-2 transition-colors ${
                    active === id
                      ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#0a0a0a]"
                      : "border-transparent text-white/50 hover:text-white hover:bg-[#0a0a0a]"
                  }`}
                  data-testid={`tutorial-toc-${id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{label}</span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Sections */}
        <main className="space-y-16">
          {SECTIONS.map((s, idx) => (
            <section
              key={s.id}
              id={s.id}
              className="scroll-mt-24"
              data-testid={`tutorial-section-${s.id}`}
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 shrink-0 border border-white/10 bg-[#121212] flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-[#6B9BFF]" />
                </div>
                <div className="min-w-0">
                  <div className="label-eyebrow text-[#6B9BFF]">{s.eyebrow}</div>
                  <h2 className="font-display text-3xl sm:text-4xl leading-tight mt-1">
                    {s.title}
                  </h2>
                </div>
              </div>
              <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-6 max-w-3xl">
                {s.summary}
              </p>
              <ul className="space-y-3 mb-7 max-w-3xl">
                {s.points.map((p, i) => (
                  <li
                    key={`${s.id}-pt-${i}`}
                    className="flex items-start gap-3 text-white/70 text-sm sm:text-base"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#6B9BFF] mt-1 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={s.cta.to}
                className="inline-flex items-center gap-2 bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white transition-colors"
                data-testid={`tutorial-cta-${s.id}`}
              >
                {s.cta.label} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              {idx < SECTIONS.length - 1 && (
                <div className="mt-16 border-t border-white/5" aria-hidden="true" />
              )}
            </section>
          ))}

          {/* Pro tips */}
          <section className="scroll-mt-24" data-testid="tutorial-section-pro-tips">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 shrink-0 border border-white/10 bg-[#121212] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#6B9BFF]" />
              </div>
              <div>
                <div className="label-eyebrow text-[#6B9BFF]">Bonus</div>
                <h2 className="font-display text-3xl sm:text-4xl leading-tight mt-1">
                  Pro tips
                </h2>
              </div>
            </div>
            <ul className="space-y-3 max-w-3xl">
              {PRO_TIPS.map((tip, i) => (
                <li key={i} className="flex items-start gap-3 text-white/70 text-sm sm:text-base">
                  <ChevronRight className="w-4 h-4 text-[#6B9BFF] mt-1 shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Quick links footer */}
          <section className="border border-white/10 bg-[#121212] p-8">
            <div className="label-eyebrow text-[#6B9BFF] mb-2">Ready to train?</div>
            <h3 className="font-display text-3xl mb-6">Jump back in.</h3>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/app/dashboard"
                className="bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white transition-colors inline-flex items-center gap-2"
                data-testid="tutorial-footer-dashboard"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Dashboard
              </Link>
              <Link
                to="/app/workout"
                className="border border-white/15 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:border-[#6B9BFF] hover:text-[#6B9BFF] transition-colors inline-flex items-center gap-2"
                data-testid="tutorial-footer-workout"
              >
                <Play className="w-3.5 h-3.5" /> Generate workout
              </Link>
              <Link
                to="/app/nutrition"
                className="border border-white/15 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:border-[#6B9BFF] hover:text-[#6B9BFF] transition-colors inline-flex items-center gap-2"
                data-testid="tutorial-footer-nutrition"
              >
                <Camera className="w-3.5 h-3.5" /> Log a meal
              </Link>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
